import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Plus, Edit, Trash2, X, AlertCircle, Loader, Paperclip, Banknote, Coins, Calculator, BadgeAlert, CheckCircle, TrendingDown, TrendingUp, FileDown } from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";
import { Textarea } from "../components/ui/textarea";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import LogoBlanco from '../components/img/LogoBlanco.png';

// Servicios
import { CierreAgenteService } from "../services/cierreAgenteService";
// Tipos de Response
import { CierreAgenteResponse} from "../types/cierreAgente";
import { DepositoBancoResponse } from '../types/depositoBanco' 
import { DepositoBancoService } from "../services/depositoBancoService";

export default function DetalleCierres({ isOpen, onClose, idEditando, fecha, monto }: { isOpen: boolean; onClose: () => void; idEditando: number, fecha: string, monto: number }) {

    const [isLoading, setIsLoading] = useState(true);
    const [cierres, setCierres] = useState<CierreAgenteResponse[]>([]);
    const [error, setError] = useState("");
    const [saldoSistema, setSaldoSistema] = useState<number>(0);
    const currentPrintRef = useRef<HTMLDivElement | null>(null);
    const printRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const [depositosPorCierre, setDepositosPorCierre] = useState<Record<number, DepositoBancoResponse[]>>({});

    const [estiloDenominacion, setEstiloDenominacion] = useState("px-4 py-2 bg-primary/10 border border-primary/0 rounded-l-lg text-center font-medium text-primary")
    const [estiloCantidad, setEstiloCantidad] = useState("px-4 py-2 bg-white border border-primary/10 rounded-r-lg text-center font-medium text-primary")

    const { addToast } = useToast();

    const handlePrint = useReactToPrint({
        contentRef: currentPrintRef,
        pageStyle: `
        @page {
            margin: 20mm;
        }
        * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
    `,
    });

    const cargarCierres = async (id: number) => {
        try {
            setIsLoading(true);
            if(fecha.length != 0){
                //console.log("NO SE: ", fecha)
                const response = await CierreAgenteService.getCierresFecha(id, fecha);
                setCierres(response.data);
            }else{
                const response = await CierreAgenteService.getCierres(id);
                setCierres(response.data);
            }
        } catch (err) {
            console.error("Error al cargar cierres: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const formatearFecha = (fecha: string) => {
        const [dia, mes, yy] = fecha.split('/');
        //console.log(`BUSCO EN EL DÍA ${yy}-${mes}-${dia}`);
        return `${yy}-${mes}-${dia}`;
    };

    const cargarSaldo = async (id: number) => {
        try {
            if(id!=0){
                const response = await CierreAgenteService.getSaldoAgente(id);
                const saldoSistema = response.data;
                setSaldoSistema(saldoSistema.saldo);
            }
        } catch (err) {
            console.error("Error al cargar empleados: " + err);
        }
    }

    useEffect(() => {
        if(isOpen) {
            if (idEditando !== 0) {
                cargarCierres(idEditando);
                cargarSaldo(idEditando);
            }
            setError("");
        }
    }, [isOpen]);

    const cargarTodasLasBoletas = async () => {
    try {
        const resultados = await Promise.all(
        cierres.map(c =>
            DepositoBancoService.getByCierreAdmin(c.idCierreAgente!)
        )
        );

        const nuevoEstado: Record<number, DepositoBancoResponse[]> = {};

        resultados.forEach((res, index) => {
        const id = cierres[index].idCierreAgente;

        nuevoEstado[id!] = res.data.map((item: any) => ({
            idDepositoBanco: item.idDepositoBanco,
            cantidad: Number(item.cantidad),
            noBoleta: item.noBoleta,
            fechaBoleta: item.fechaBoleta,
            banco: item.banco,
            idAgente: idEditando,
            activo: item.activo
        }));
        });

        setDepositosPorCierre(nuevoEstado);

    } catch {
        setError("Error al cargar boletas");
    }
    };

    useEffect(() => {
    if (cierres.length > 0) {
        cargarTodasLasBoletas();
    }
    }, [cierres]);

      const getEstadoColor = (estado: string) => {
        switch (estado) {
        case 'Cuadrado':
            return 'text-accent';
        case 'Faltante':
            return 'text-destructive';
        case 'Sobrante':
            return 'text-[#dea753]';
        case 'Pendiente':
            return 'text-muted-foreground';
        default:
            return 'text-foreground';
        }
    };

    const getEstadoIcon = (estado: string) => {
        switch (estado) {
        case 'Cuadrado':
            return <CheckCircle className="w-4 h-4" />;
        case 'Faltante':
            return <TrendingDown className="w-4 h-4" />;
        case 'Sobrante':
            return <TrendingUp className="w-4 h-4" />;
        case 'Pendiente':
            return <AlertCircle className="w-4 h-4" />;
        default:
            return null;
        }
    };

    if (!isOpen) {
        return null;
    }
    return (
        <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-2xl w-full max-w-2xl shadow-lg relative max-h-[90vh] overflow-y-auto scrollbar-thin">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Detalles del Cierre</CardTitle>
                        <button
                        onClick={onClose}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        >
                        <X className="w-5 h-5 text-destructive" />
                        </button>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                        <div className='grid grid-cols-1 md:grid-cols-1 gap-4 items-end'>
                            {cierres.map((c) => {
                                const totalBilletes =
                                    c.totalBilletes200 * 200 +
                                    c.totalBilletes100 * 100 +
                                    c.totalBilletes50 * 50 +
                                    c.totalBilletes20 * 20 +
                                    c.totalBilletes10 * 10 +
                                    c.totalBilletes5 * 5 +
                                    c.totalBilletes1 * 1;

                                const totalMonedas =
                                    c.totalMonedas1 * 1 +
                                    c.totalMonedas050 * 0.5 +
                                    c.totalMonedas025 * 0.25 +
                                    c.totalMonedas010 * 0.1 +
                                    c.totalMonedas005 * 0.05 +
                                    c.totalMonedas001 * 0.01;

                                const efectivoFisico = totalBilletes + totalMonedas;

                                const depositoBancoList = depositosPorCierre[c.idCierreAgente!] || [];

                                const totalBoletas = depositoBancoList.reduce(
                                    (acc, d) => acc + Number(d.cantidad),
                                    0
                                );

                                const suma = efectivoFisico + totalBoletas - Number(c.saldoSistema);

                                let sobregirado = false;
                                if(Number(c.saldoSistema) >= monto){sobregirado = true}
                                if(efectivoFisico + totalBoletas >= monto){sobregirado = true}

                                let estado = ""
                                if(suma > 0){
                                    estado = "Sobrante"
                                }else if (suma < 0){
                                    estado = "Faltante"
                                }else{
                                    estado = "Cuadrado"
                                }

                            return (
                                <div key={c.idCierreAgente} className="mb-5" ref={(el) => {printRefs.current[c.idCierreAgente!] = el;}}>
                                <div className="grid grid-cols-6 gap-4 p-5 md:p-6 text-white bg-primary border border-primary rounded-t-lg items-center">

                                {/* Logo */}
                                <div className="col-span-2 flex justify-center md:justify-start">
                                    <img 
                                    src={LogoBlanco} 
                                    alt="Logo AGEMI" 
                                    className="w-20 md:w-40 h-auto object-contain"
                                    />
                                </div>

                                {/* Información 1 */}
                                <div className="col-span-2 flex flex-col text-sm md:text-base">
                                    <span className="opacity-80">Código</span>
                                    <span className="font-semibold">#CA-{c.idCierreAgente}</span>

                                    <span className="opacity-80 mt-2">Versión</span>
                                    <span className="font-semibold">{c.version}</span>
                                </div>

                                {/* Información 2 */}
                                <div className="col-span-2 flex flex-col text-sm md:text-base">
                                    <span className="opacity-80">Fecha del cierre</span>
                                    <span className="font-semibold">
                                    {c.fecha.slice(0,10).split("-").reverse().join("/")}
                                    </span>

                                    <span className="opacity-80 mt-2">Creado</span>
                                    <span className="font-semibold">
                                    {new Date(c.fechaCreado!).toLocaleString('es-ES')}
                                    </span>
                                </div>

                                </div>
                                    <div className="grid grid-cols-2 mt-5 mb-5">
                                        <div className="flex items-center justify-center gap-2">
                                            <Banknote className="w-5 h-5 text-primary" />
                                            <CardTitle>Billetes</CardTitle>
                                        </div>

                                        <div className="flex items-center justify-center gap-2">
                                            <Coins className="w-5 h-5 text-primary" />
                                            <CardTitle>Monedas</CardTitle>
                                        </div>
                                    </div>
                                    <div className='grid grid-cols-2 gap-4 items-end'>
                                        <div className='grid grid-cols-2 gap-y-4 items-end'>
                                            <div className={estiloDenominacion}>
                                                Q200.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes200}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q100.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes100}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q50.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes50}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q20.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes20}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q10.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes10}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q5.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes5}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q1.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalBilletes1}
                                            </div>
                                        </div>
                                        
                                        <div className='grid grid-cols-2 gap-y-4 items-end'>
                                            <div className={estiloDenominacion}>
                                                Q1.00
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas1}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q0.50
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas050}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q0.25
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas025}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q0.10
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas010}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q0.05
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas005}
                                            </div>
                                            <div className={estiloDenominacion}>
                                                Q0.01
                                            </div>
                                            <div className={estiloCantidad}>
                                                {c.totalMonedas001}
                                            </div>
                                            <div className="px-4 py-2 bg-primary/10 border border-primary/0 rounded-lg text-center font-medium text-primary invisible">f</div>
                                            <div className="px-4 py-2 bg-white border border-primary/20 rounded-lg text-center font-medium text-primary invisible">f</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 mt-7 mb-3 pb-3">
                                        <Calculator className="w-5 h-5 text-primary" />
                                        <CardTitle>Resultado del Cierre:</CardTitle>
                                    </div>
                                    <div className='grid grid-cols-2 gap-4 items-end mb-5'>
                                        <div className="py-2 text-start font-medium">
                                            Saldo Según Sistema:
                                        </div>
                                        <div>
                                            <div className="px-4 py-2 bg-primary/10 border border-primary/0 rounded-lg text-end font-medium text-primary">
                                            Q{Number(c.saldoSistema).toLocaleString('es-GT', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                                            </div>
                                        </div>    
                                        <div className="py-2 text-start font-medium">
                                            Efectivo Físico:
                                        </div>
                                        <div>
                                            <div className="px-4 py-2 bg-accent/10 border border-accent/0 rounded-lg text-end font-medium text-accent">
                                            Q{Number(efectivoFisico).toLocaleString('es-GT', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                            })}
                                            </div>
                                        </div> 
                                        <div className="py-2 text-start font-medium col-span-2">
                                            Boletas de Banco:
                                        </div>
                                        <div>
                                            {
                                                depositoBancoList.length === 0 ? (
                                                <div className="py-2 text-start align-bottom">
                                                    <p className="text-sm text-muted-foreground">No hay boletas</p>
                                                </div>
                                                ) : (
                                                depositoBancoList.map((deposito) => (
                                                    <div key={deposito.idDepositoBanco} className="py-2 flex items-center justify-between">
                                                    <div className="flex flex-col text-sm text-muted-foreground">
                                                        <div className="flex gap-6">
                                                        <span>No: {deposito.noBoleta}</span>
                                                        <span>
                                                            Fecha: {(deposito.fechaBoleta).slice(0,10).split("-").reverse().join("/")}
                                                        </span>
                                                        </div>

                                                        <span>Banco: {deposito.banco}</span>
                                                    </div>
                                                    </div>
                                                ))
                                                )
                                            }
                                        </div>

                                        <div>
                                            {
                                                depositoBancoList.length === 0 ? (
                                                <div className="py-2"></div>
                                                ) : (
                                                depositoBancoList.map((deposito) => (
                                                    <div key={deposito.idDepositoBanco} className="py-2">
                                                    <div className="px-4 py-2 bg-accent/10 rounded-lg text-end font-medium text-accent">
                                                        Q{Number(deposito.cantidad).toLocaleString('es-GT', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2
                                                        })}
                                                    </div>
                                                    </div>
                                                ))
                                                )
                                            }
                                        </div>

                                        <div className="pt-4 border-t-1 border-primary col-span-2">
                                            <div className="flex justify-between items-center">
                                                <span className="font-bold">Diferencia:</span>
                                                <span className={`font-bold px-4 ${getEstadoColor(
                                            estado
                                            )}`}>
                                                    Q{suma != null && suma > 0 ? '+' : ''}{suma.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>   
                                        <div
                                            className={`pt-4 flex col-span-2 font-bold gap-4 justify-end items-end px-4 ${getEstadoColor(
                                            estado
                                            )}`}
                                        >
                                            {getEstadoIcon(estado)}
                                            {estado}
                                        </div>
                                        {sobregirado && (
                                            <div className = "pt-4 flex col-span-2 gap-4 justify-center items-center text-destructive">
                                                <BadgeAlert/>
                                                <span className={`font-bold px-8`}>
                                                    SOBREGIRADO
                                                </span>
                                            </div>
                                        )}                               
                                    </div>
                                    <div className={`grid grid-cols-3 gap-4 items-end align-top`}>
                                        <div className="py-2 text-start font-medium">
                                            Comentario del Operador:
                                        </div>
                                        <div className='col-span-2'>
                                            <Textarea
                                            className="bg-white text-start"
                                            value={c.comentario}
                                            disabled
                                            />
                                        </div>
                                    </div>
                                    <div className={`grid grid-cols-3 gap-4 items-end align-top`}>
                                        <div className="py-2 text-start font-medium">
                                            Observación:
                                        </div>
                                        <div className='col-span-2'>
                                            <Textarea
                                            className="bg-white text-start"
                                            value={c.observacion}
                                            disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="pt-4 flex col-span-2 font-bold gap-4 justify-end items-end px-4">
                                        <Button 
                                            className="print:hidden"
                                            variant="accent"
                                            onClick={() => {
                                                currentPrintRef.current = printRefs.current[c.idCierreAgente!];

                                                document.title = `CIERRE #CA-${c.idCierreAgente} ${new Date().toLocaleDateString()}`;

                                                handlePrint();
                                            }}
                                            >
                                            <FileDown className="w-4 h-4 mr-2" />
                                            <span className="hidden sm:inline">Exportar PDF</span>
                                            <span className="sm:hidden">PDF</span>
                                        </Button>
                                    </div>   
                                </div>
                            )})}
                        </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )

}